import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import WelcomeScene from '../components/WelcomeScene';
import { useAuth } from '../context/AuthContext';

// ── IT Head daily dashboard ────────────────────────────────────────────────
// One call, /api/dashboard, then a page whose top band is only things needing
// a person today. Everything is clickable through to the list behind it —
// a number you cannot open is a dead end, and people stop trusting it the
// first time it disagrees with the Tasks page.
//
// Styling uses the portal's own tokens (var(--bg2), var(--red) …) so it sits
// inside the app rather than beside it. The earlier dark console looked
// striking on its own and wrong in place — and worse, its table inherited the
// app's dark text onto a dark ground, which hid half the numbers.

const NUM = { fontVariantNumeric: 'tabular-nums' };

// "4d 6h" reads better than "102 hours" when deciding what to chase.
function age(hours) {
  if (hours == null) return '—';
  const h = Math.round(hours);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

const TONE = { red: 'var(--red)', amber: 'var(--amber)', green: 'var(--green)', flat: 'var(--text2)' };

function SectionLabel({ children, note }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
      <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.08em',
                     textTransform: 'uppercase', color: 'var(--text2)' }}>{children}</span>
      {note && <span style={{ fontSize: 12.5, color: 'var(--text3)' }}>{note}</span>}
    </div>
  );
}

function Panel({ title, right, children, style }) {
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12,
                                   minWidth: 0, padding: 18, ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>{title}</span>
        {right}
      </div>
      {children}
    </div>
  );
}

// A KPI tile: the whole card is the link into the list behind the number.
function Tile({ label, value, sub, tone = 'flat', onClick }) {
  const colour = TONE[tone];
  return (
    <button type="button" onClick={onClick} className="card"
      style={{ textAlign: 'left', cursor: onClick ? 'pointer' : 'default', font: 'inherit',
               display: 'flex', flexDirection: 'column', gap: 4, padding: '16px 18px',
               borderLeft: `4px solid ${colour}`, color: 'var(--text)', minHeight: 44 }}>
      <span style={{ fontSize: 13, color: 'var(--text2)' }}>{label}</span>
      <span style={{ ...NUM, fontSize: typeof value === 'number' ? 34 : 22, fontWeight: 700,
                     lineHeight: 1.1, color: colour }}>{value}</span>
      <span style={{ ...NUM, fontSize: 12.5, color: 'var(--text2)' }}>{sub}</span>
    </button>
  );
}

function Bar({ label, pct }) {
  // null means nothing was due — which must not look like nobody did it.
  const known = pct != null;
  const colour = !known ? 'var(--text3)' : pct >= 85 ? 'var(--green)'
               : pct >= 60 ? 'var(--amber)' : 'var(--red)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <span style={{ width: 168, fontSize: 13.5, color: 'var(--text2)' }}>{label}</span>
      <span style={{ flexGrow: 1, height: 9, background: 'var(--bg3)', borderRadius: 999 }}>
        <span style={{ display: 'block', width: `${known ? pct : 0}%`, height: 9,
                       background: colour, borderRadius: 999 }} />
      </span>
      <span style={{ ...NUM, width: 64, textAlign: 'right', fontSize: 13.5, fontWeight: 700,
                     color: known ? 'var(--text)' : 'var(--text3)' }}>
        {known ? `${pct}%` : 'no data'}
      </span>
    </div>
  );
}

function Pill({ colour, children }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', color: colour,
                   border: `1px solid ${colour}`, borderRadius: 999, padding: '3px 9px',
                   whiteSpace: 'nowrap' }}>{children}</span>
  );
}

// A plain link that looks like a link and is one.
function Jump({ onClick, children, bold = true }) {
  return (
    <button type="button" onClick={onClick}
      style={{ font: 'inherit', fontWeight: bold ? 600 : 400, background: 'none', border: 'none',
               color: 'var(--accent)', cursor: 'pointer', padding: 0, textAlign: 'left' }}>
      {children}
    </button>
  );
}

export default function CommandCenter({ go }) {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setBusy(true);
    api.get('/dashboard')
      .then(r => { setD(r.data); setErr(''); })
      .catch(e => setErr(e.response?.data?.error || 'Could not load the dashboard'))
      .finally(() => setBusy(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="err-msg" style={{ margin: 4 }}>{err}</div>;
  if (!d) return <div style={{ padding: 20, color: 'var(--text2)' }}>Loading…</div>;

  const h = d.headline || {};
  const ap = d.approvals || {};
  const approvalTotal = (ap.registrations || 0) + (ap.class_teachers || 0) + (ap.delays || 0);
  const backup = d.backup;
  // A backup row older than 36h means last night's run never happened.
  const backupOk = backup && backup.recent && backup.status === 'success';
  const unassignedTop = (d.unassigned_by_campus || [])[0];
  const comp = d.compliance || {};
  const fb = d.staff_feedback || {};
  const ai = d.ai_feedback || {};
  const vacant = (d.campuses || []).filter(c => !c.staff && c.open > 0);

  const hhmm = new Date(d.generated_at).toLocaleTimeString('en-IN',
    { hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Once per sign-in, for IT only. It removes itself after that. */}
      <WelcomeScene go={go} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
                    gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.01em' }}>Command Center</h1>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 3 }}>
            {new Date().toLocaleDateString('en-IN',
              { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
            {' · '}{d.scope === 'all' ? `${(d.campuses || []).length} campuses` : 'my campuses'}
            {' · '}updated {hhmm}
          </div>
        </div>
        <div className="flex-gap" style={{ gap: 8 }}>
          {/* An HR mail arrives, the admin opens the portal and starts here. */}
          {can('onboarding', 'create') && (
            <button className="btn btn-primary" onClick={() => go && go('onboarding', 'new')}>
              + New Staff Onboard
            </button>
          )}
          <button className="btn btn-secondary" onClick={load} disabled={busy}>
            {busy ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* ── Needs you today ── */}
      <div>
        <SectionLabel note="each figure opens the list behind it">Needs you today</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14 }}>
          <Tile label="SLA breached" value={h.sla_breached || 0}
                tone={h.sla_breached ? 'red' : 'green'}
                sub={`oldest open ${age(h.oldest_open_h)}`} onClick={() => go && go('tasks')} />
          <Tile label="Unassigned > 4h" value={h.unassigned || 0}
                tone={h.unassigned ? 'red' : 'green'}
                sub={unassignedTop ? `${unassignedTop.n} at ${unassignedTop.campus}` : 'none waiting'}
                onClick={() => go && go('tasks')} />
          <Tile label="Waiting on you" value={approvalTotal}
                tone={approvalTotal ? 'amber' : 'green'}
                sub={ap.oldest_reg_days ? `oldest ${ap.oldest_reg_days} days` : 'approvals pending'}
                onClick={() => go && go('approvals')} />
          <Tile label="Routines missed" value={(d.routines_missed || {}).n || 0}
                tone={(d.routines_missed || {}).n ? 'amber' : 'green'}
                sub="past their time today" onClick={() => go && go('routines')} />
          <Tile label="Policies unread" value={(d.acks_overdue || {}).n || 0}
                tone={(d.acks_overdue || {}).n ? 'amber' : 'green'}
                sub="past due date" onClick={() => go && go('policies')} />
          <Tile label={backup ? `Backup ${backup.at.slice(11)}` : 'Backup'}
                value={!backup ? 'Never run' : !backup.recent ? 'Stale'
                       : backupOk ? 'Clean' : 'Failed'}
                tone={backupOk ? 'green' : 'red'}
                sub={!backup ? 'no backup has ever run'
                     : !backup.recent ? `last ran ${backup.at.slice(0, 10)}`
                     : backup.drive_status === 'uploaded' ? 'Drive copy ok'
                     : `Drive: ${backup.drive_status || 'skipped'}`}
                onClick={() => go && go('backup')} />
        </div>
      </div>

      {/* ── Approvals · campuses ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: 16, alignItems: 'start' }}>

        <Panel title="Approval centre"
               right={<Jump onClick={() => go && go('approvals')}>Open →</Jump>}
               style={{ gridColumn: 'span 1' }}>
          {[['New registrations', ap.registrations,
             ap.oldest_reg_days ? `oldest waiting ${ap.oldest_reg_days} days` : 'nothing waiting'],
            ['Class-teacher mappings', ap.class_teachers, 'awaiting your approval'],
            ['Routine delay requests', ap.delays, 'reasons to review']].map(([label, n, sub]) => (
            <button key={label} type="button" onClick={() => go && go('approvals')}
              style={{ font: 'inherit', textAlign: 'left', cursor: 'pointer', display: 'flex',
                       alignItems: 'center', justifyContent: 'space-between', gap: 10,
                       padding: '12px 14px', minHeight: 44, background: 'var(--bg3)',
                       border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                       color: 'var(--text)' }}>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 13.5, fontWeight: 600 }}>{label}</span>
                <span style={{ fontSize: 12, color: 'var(--text2)' }}>{sub}</span>
              </span>
              <span style={{ ...NUM, fontSize: 22, fontWeight: 700,
                             color: n ? 'var(--amber)' : 'var(--text3)' }}>{n || 0}</span>
            </button>
          ))}
          <div style={{ fontSize: 12.5, color: 'var(--text2)', marginTop: 'auto' }}>
            The only things on this page blocked on you personally.
          </div>
        </Panel>

        <Panel title="Campus comparison · this week"
               right={<span style={{ fontSize: 12.5, color: 'var(--text2)' }}>worst first</span>}
               style={{ gridColumn: 'span 2', minWidth: 420 }}>
          <div className="table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
              <thead>
                <tr style={{ textAlign: 'left', background: 'var(--bg3)' }}>
                  {['Campus', 'IT staff', 'Open', 'Overdue', 'Closed', 'Oldest', 'vs last week']
                    .map((t, i) => (
                    <th key={t} style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--text2)',
                                         textAlign: i >= 2 && i <= 5 ? 'right' : 'left',
                                         whiteSpace: 'nowrap' }}>{t}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(d.campuses || []).filter(c => c.open || c.closed_week).slice(0, 8).map(c => {
                  const delta = (c.open || 0) - (c.open_last_week || 0);
                  return (
                    <tr key={c.id} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '11px 12px' }}>
                        <Jump onClick={() => go && go('tasks')}>{c.campus}</Jump>
                      </td>
                      <td style={{ padding: '11px 12px', color: c.staff ? 'var(--text2)' : 'var(--red)',
                                   fontWeight: c.staff ? 400 : 600 }}>{c.staff || 'Vacant'}</td>
                      <td style={{ ...NUM, padding: '11px 12px', textAlign: 'right', fontWeight: 600,
                                   color: 'var(--text)' }}>{c.open}</td>
                      <td style={{ ...NUM, padding: '11px 12px', textAlign: 'right',
                                   fontWeight: c.overdue ? 700 : 400,
                                   color: c.overdue ? 'var(--red)' : 'var(--text3)' }}>{c.overdue}</td>
                      <td style={{ ...NUM, padding: '11px 12px', textAlign: 'right',
                                   color: 'var(--text)' }}>{c.closed_week}</td>
                      <td style={{ ...NUM, padding: '11px 12px', textAlign: 'right',
                                   color: 'var(--text2)', whiteSpace: 'nowrap' }}>{age(c.oldest_open_h)}</td>
                      <td style={{ ...NUM, padding: '11px 12px', whiteSpace: 'nowrap',
                                   fontWeight: delta ? 600 : 400,
                                   color: delta > 0 ? 'var(--red)' : delta < 0 ? 'var(--green)' : 'var(--text2)' }}>
                        {delta > 0 ? `↑ ${delta} more open`
                          : delta < 0 ? `↓ ${-delta} fewer open` : 'no change'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!!vacant.length && (
            <div style={{ fontSize: 12.5, color: 'var(--red)' }}>
              {vacant.length === 1
                ? `${vacant[0].campus} has no IT staff assigned and ${vacant[0].open} open tickets.`
                : `${vacant.length} campuses have no IT staff assigned: ${vacant.map(v => v.campus).join(', ')}.`}
            </div>
          )}
        </Panel>
      </div>

      {/* ── Projects · next 7 days ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: 16, alignItems: 'start' }}>

        <Panel title="Projects · by health"
               right={<Jump onClick={() => go && go('containers')}>View all →</Jump>}
               style={{ gridColumn: 'span 2', minWidth: 420 }}>
          {!(d.projects || []).length
            ? <div style={{ fontSize: 13.5, color: 'var(--text2)' }}>No open projects.</div>
            : (
            <div className="table-wrap">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                <tbody>
                  {(d.projects || []).map(p => {
                    // A flag nobody has touched in a fortnight is not evidence
                    // of health — show it as stale, not a reassuring green.
                    const stale = (p.flag_age_days || 0) >= 14;
                    const colour = stale ? 'var(--text2)'
                                 : p.health_flag === 'red' ? 'var(--red)'
                                 : p.health_flag === 'amber' ? 'var(--amber)' : 'var(--green)';
                    const label = stale ? `Stale ${p.flag_age_days}d`
                                : p.health_flag === 'red' ? 'Delayed'
                                : p.health_flag === 'amber' ? 'At risk' : 'On track';
                    const pct = p.total_tasks
                      ? Math.round(100 * (p.total_tasks - p.open_tasks) / p.total_tasks) : null;
                    const late = p.days_left != null && p.days_left < 0;
                    return (
                      <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px 12px 12px 0' }}>
                          <Jump onClick={() => go && go('containers')}>{p.title}</Jump>
                          <div style={{ fontSize: 12, color: 'var(--text2)' }}>{p.owner || 'no owner'}</div>
                        </td>
                        <td style={{ padding: '12px', width: 160 }}>
                          {pct == null
                            ? <span style={{ fontSize: 12, color: 'var(--text3)' }}>no tasks yet</span>
                            : (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ flexGrow: 1, height: 7, background: 'var(--bg3)',
                                             borderRadius: 999 }}>
                                <span style={{ display: 'block', width: `${pct}%`, height: 7,
                                               background: colour, borderRadius: 999 }} />
                              </span>
                              <span style={{ ...NUM, fontSize: 12, color: 'var(--text2)' }}>{pct}%</span>
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}><Pill colour={colour}>{label}</Pill></td>
                        <td style={{ ...NUM, padding: '12px 0 12px 12px', textAlign: 'right',
                                     whiteSpace: 'nowrap',
                                     color: late ? 'var(--red)' : 'var(--text2)',
                                     fontWeight: late ? 600 : 400 }}>
                          {p.target_date
                            ? <>{p.target_date}{p.days_left != null &&
                                ` · ${late ? `${-p.days_left}d late` : `${p.days_left}d`}`}</>
                            : 'no target'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ fontSize: 12.5, color: 'var(--text2)' }}>
            Health is the owner's own flag. Untouched for 14 days it reads stale, never green.
          </div>
        </Panel>

        <Panel title="Next 7 days"
               right={<Jump onClick={() => go && go('calendar')}>Calendar →</Jump>}>
          {!(d.upcoming || []).length &&
            <div style={{ fontSize: 13.5, color: 'var(--text2)' }}>Nothing scheduled.</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            {(d.upcoming || []).map((u, i) => {
              const colour = { meeting: 'var(--accent)', training: 'var(--amber)',
                               routine: 'var(--green)' }[u.kind] || 'var(--purple)';
              return (
                <div key={i} style={{ display: 'flex', gap: 11, alignItems: 'flex-start',
                                      paddingLeft: 11, borderLeft: `3px solid ${colour}` }}>
                  <span style={{ ...NUM, fontSize: 12.5, fontWeight: 700, color: 'var(--text2)',
                                 width: 54, flexShrink: 0 }}>
                    {new Date(u.on_date + 'T00:00:00')
                      .toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit' })}
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 600 }}>{u.title}</span>
                    <span style={{ fontSize: 12, color: 'var(--text2)' }}>
                      {u.at_time ? `${u.at_time} · ` : ''}{u.kind}
                      {u.n > 1 ? ` · ${u.n} campuses` : ''}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
          {!!(d.unlogged_calendar || {}).n && (
            <button type="button" onClick={() => go && go('calendar')}
              style={{ font: 'inherit', textAlign: 'left', cursor: 'pointer', marginTop: 'auto',
                       display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                       gap: 10, padding: '12px 14px', minHeight: 44, background: 'var(--bg3)',
                       border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                       color: 'var(--text)' }}>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>Not logged as work yet</span>
                <span style={{ fontSize: 12, color: 'var(--text2)' }}>from your Google calendar</span>
              </span>
              <span style={{ ...NUM, fontSize: 22, fontWeight: 700, color: 'var(--amber)' }}>
                {d.unlogged_calendar.n}
              </span>
            </button>
          )}
        </Panel>
      </div>

      {/* ── Feedback: the service as the people using it see it ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
                    gap: 16, alignItems: 'start' }}>

        <Panel title="Staff feedback"
               right={<Jump onClick={() => go && go('feedback')}>All feedback →</Jump>}>
          {!fb.total ? (
            <div style={{ fontSize: 13.5, color: 'var(--text2)' }}>
              No ratings in the last 90 days.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
                <span style={{ ...NUM, fontSize: 34, fontWeight: 700,
                               color: fb.avg_rating >= 4 ? 'var(--green)'
                                    : fb.avg_rating >= 3 ? 'var(--amber)' : 'var(--red)' }}>
                  {fb.avg_rating}
                </span>
                <span style={{ fontSize: 13, color: 'var(--text2)' }}>
                  out of 5, from {fb.total} rating{fb.total === 1 ? '' : 's'} in 90 days
                  {' · '}{fb.last_30} in the last 30
                </span>
              </div>
              <div style={{ display: 'flex', gap: 18, marginTop: 4, fontSize: 13 }}>
                <span style={{ color: 'var(--green)' }}>
                  <b className="num">{fb.happy}</b> happy (4–5)
                </span>
                <span style={{ color: fb.unhappy ? 'var(--red)' : 'var(--text2)' }}>
                  <b className="num">{fb.unhappy}</b> unhappy (1–2)
                </span>
              </div>

              {/* The comments behind a low score — the single most useful
                  thing on this page, and previously buried in a screen
                  nobody opens. */}
              {!!(d.unhappy_feedback || []).length && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                                letterSpacing: '.05em', color: 'var(--text2)', marginBottom: 8 }}>
                    Needs a word
                  </div>
                  {(d.unhappy_feedback || []).map((x, i) => (
                    <div key={i} style={{ marginBottom: 9 }}>
                      <div style={{ fontSize: 13 }}>
                        <span style={{ color: 'var(--red)', fontWeight: 700 }}>
                          {'★'.repeat(x.rating)}{'☆'.repeat(5 - x.rating)}
                        </span>{' '}
                        <Jump onClick={() => go && go('tasks')} bold={false}>{x.task_id}</Jump>
                        <span style={{ color: 'var(--text2)' }}>
                          {' · '}{x.campus_name || '—'}{' · '}{x.on_date}
                        </span>
                      </div>
                      {x.comment && (
                        <div style={{ fontSize: 12.5, color: 'var(--text2)', fontStyle: 'italic' }}>
                          “{x.comment}”
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Panel>

        <Panel title="AI assistant · last 30 days"
               right={<Jump onClick={() => go && go('ailogs')}>AI logs →</Jump>}>
          {!ai.asked_30 ? (
            <div style={{ fontSize: 13.5, color: 'var(--text2)' }}>
              Nobody has asked the assistant anything in the last 30 days.
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px,1fr))',
                            gap: 12 }}>
                {[['Questions', ai.asked_30, 'var(--text)'],
                  ['People asking', ai.people, 'var(--text)'],
                  ['Rated helpful', ai.helpful_pct == null ? '—' : `${ai.helpful_pct}%`,
                    ai.helpful_pct == null ? 'var(--text3)'
                      : ai.helpful_pct >= 70 ? 'var(--green)'
                      : ai.helpful_pct >= 40 ? 'var(--amber)' : 'var(--red)'],
                  ['Typical reply', ai.avg_ms ? `${(ai.avg_ms / 1000).toFixed(1)}s` : '—',
                    'var(--text2)']].map(([k, v, colour]) => (
                  <div key={k}>
                    <div style={{ fontSize: 11.5, color: 'var(--text2)' }}>{k}</div>
                    <div style={{ ...NUM, fontSize: 22, fontWeight: 700, color: colour }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)',
                            fontSize: 12.5, color: 'var(--text2)' }}>
                {ai.helpful + ai.not_helpful === 0
                  ? 'Nobody has rated an answer yet, so the helpfulness figure is unknown — '
                    + 'not zero.'
                  : `${ai.helpful} marked helpful, ${ai.not_helpful} not, out of `
                    + `${ai.asked_30} questions.`}
              </div>
            </>
          )}
        </Panel>
      </div>

      {/* ── Compliance · systems ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
                    gap: 16, alignItems: 'start' }}>

        <Panel title="Compliance this week"
               right={<Jump onClick={() => go && go('performance')}>Work report →</Jump>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Bar label="Routines completed"    pct={comp.routines_pct} />
            <Bar label="Policies acknowledged" pct={comp.policies_pct} />
            <Bar label="Training completed"    pct={comp.training_pct} />
            <Bar label="Health checks healthy" pct={comp.health_pct} />
          </div>
          <div style={{ paddingTop: 12, borderTop: '1px solid var(--border)',
                        fontSize: 12.5, color: 'var(--text2)' }}>
            “No data” means nothing was due this week — not that nobody did it.
          </div>
        </Panel>

        <Panel title="Systems"
               right={<Pill colour={backupOk ? 'var(--green)' : 'var(--amber)'}>
                        {backupOk ? 'Stable' : 'Needs a look'}
                      </Pill>}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                        gap: 10 }}>
            {[
              ['Nightly backup',
                !backup ? 'Never run' : !backup.recent ? 'Stale'
                  : backup.status === 'success' ? 'OK' : 'Failed',
                !backup || !backup.recent || backup.status !== 'success' ? 'var(--red)' : 'var(--green)'],
              ['Drive off-site copy',
                backup ? (backup.drive_status || 'skipped') : '—',
                backup && backup.drive_status === 'uploaded' ? 'var(--green)' : 'var(--amber)'],
              ['Warranty expiring', `${(d.warranty || {}).n || 0} assets`,
                (d.warranty || {}).n ? 'var(--amber)' : 'var(--text2)'],
              ['Open tickets', String(h.open || 0), 'var(--text2)'],
            ].map(([label, value, colour]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center',
                                        justifyContent: 'space-between', gap: 8,
                                        padding: '11px 13px', background: 'var(--bg3)',
                                        border: '1px solid var(--border)',
                                        borderRadius: 'var(--radius)' }}>
                <span style={{ fontSize: 13, color: 'var(--text2)' }}>{label}</span>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: colour,
                               textTransform: 'capitalize' }}>{value}</span>
              </div>
            ))}
          </div>
          <div style={{ paddingTop: 12, borderTop: '1px solid var(--border)',
                        fontSize: 12.5, color: 'var(--text2)', marginTop: 'auto' }}>
            A check that did not run reads “Stale” or “Never run”, never a green OK —
            a silent pass is how a broken backup hides.
          </div>
        </Panel>
      </div>

    </div>
  );
}
